import React from "react";
import { Alert } from "antd";
import { observer } from "mobx-react-lite";
import { useStores } from "../../../../stores/setup/use-store";
import { StoreState } from "../../../../enum/StoreState";
import { useTranslation } from "react-i18next";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Button,
  FormControl,
  FormLabel,
  Select,
  Heading,
  Text,
  Flex,
  Stack,
} from "@chakra-ui/react";
import { MapPin } from "lucide-react";
import { Input } from "../../../../components/Input/Input";
import { IStation } from "../../../../stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { IMacroRegion } from "../../../../types/IMacroRegion";
import Loading from "../../../../components/Loading/Loading";
import { LocationFormValues } from "../types";
import { LocationTypeSelector } from "./LocationTypeSelector";
import { BackButton } from "../../../../components/Map/BackButton";
import { useLocationTypeSelection } from "../hooks/useLocationTypeSelection";
import { useLocationFieldHandlers } from "../hooks/useLocationFieldHandlers";
import { useLocationGeofence } from "../hooks/useLocationGeofence";
import { useLocationTypeConfirmation } from "../hooks/useLocationTypeConfirmation";
import { GeofenceMapContainer } from "../../../../components/Map/GeofenceMapContainer";
import LocationTypeChangeModal from "./LocationTypeChangeModal";

interface OperationRuleLocationDrawerProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (values: LocationFormValues, isEditing?: boolean) => void;
  confirmButtonText?: string;
  initialRef?: React.RefObject<HTMLInputElement>;
  formik?: any;
  isEditing: boolean;
}

const OperationRuleLocationDrawer: React.FC<OperationRuleLocationDrawerProps> =
  ({
    onConfirm,
    title,
    isOpen,
    onClose,
    formik,
    confirmButtonText,
    isEditing,
  }) => {
    const {
      dataStores: {
        operationRuleLocationStore,
        oceanWeatherIntelligenceStore,
        regionStore,
        authStore,
      },
    } = useStores();

    const { values, handleChange } = formik;
    const { t } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.operationRuleLocation",
    });

    const { locationType, handleBack, handleLocationTypeSelect } =
      useLocationTypeSelection({
        isEditing,
        isOpen,
        operationRuleLocation: operationRuleLocationStore.operationRuleLocation,
        formik,
        oceanWeatherIntelligenceStore,
      });

    const { handleChangeRegion, handleChangeStation } =
      useLocationFieldHandlers({
        formik,
        oceanWeatherIntelligenceStore,
      });

    const {
      areaSelectorVisible,
      selectedGeofence,
      areas,
      handleDrawingMode,
      handleGeoJsonLoaded,
      clearGeofence,
      handleEnableAreaSelector,
      handleAreaSelection,
      setIsGeofenceLoadedByGeoJson,
      isGeofenceLoadedByGeoJson,
      isGeofenceInvalid,
    } = useLocationGeofence({ formik, isEditing });

    const { isModalOpen, onModalClose, handleSaveClick, handleConfirmChange } =
      useLocationTypeConfirmation({
        isEditing,
        values,
        locationType,
        onConfirm,
      });

    return (
      <>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          size="xl"
          closeOnOverlayClick={false}
        >
          <DrawerOverlay />
          <DrawerContent bg="white">
            <DrawerCloseButton color={"black"} />
            <DrawerHeader>
              <Heading display={"flex"} size="md" color="black" gap="16px">
                <MapPin size="24px" />
                <Text mt={"3px"}>{title}</Text>
              </Heading>
            </DrawerHeader>

            <DrawerBody color="black">
              <Stack spacing={6}>
                {operationRuleLocationStore.state === StoreState.ERROR && (
                  <Alert
                    message="Error"
                    description={operationRuleLocationStore.messageError}
                    type="error"
                    showIcon
                    closable
                  />
                )}
                {oceanWeatherIntelligenceStore.state === StoreState.PEDDING && (
                  <Loading styleSpinner={{ marginLeft: 0 }} />
                )}

                <FormControl isRequired>
                  <FormLabel
                    color="newBlue.500"
                    fontSize={"md"}
                    fontWeight={500}
                  >
                    {t("name")}
                  </FormLabel>
                  <Input
                    autoFocus
                    placeholder={t("name") as string}
                    maxLength={60}
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    disabled={authStore.readOnlyUser}
                  />
                </FormControl>

                {!locationType && (
                  <LocationTypeSelector
                    onSelect={handleLocationTypeSelect}
                    t={t}
                  />
                )}

                {locationType === "point" && (
                  <Box borderWidth="1px" borderRadius="md" p={4}>
                    <Flex gap="12px" mb="16px" justifyContent="space-between">
                      <Flex gap="12px">
                        <MapPin color="#086AA9" size="20px" />
                        <Text fontWeight={"400"} fontSize="md" color="#086AA9">
                          {t("pointDetails")}
                        </Text>
                      </Flex>
                      <BackButton
                        onClick={handleBack}
                        label={t("back") as string}
                      />
                    </Flex>

                    <Stack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel
                          color="newBlue.500"
                          fontSize={"md"}
                          fontWeight={500}
                        >
                          {t("region")}
                        </FormLabel>
                        <Select
                          borderRadius={8}
                          color={
                            values.macroRegionId
                              ? "rgba(30, 82, 151, 0.8)"
                              : "gray.400"
                          }
                          backgroundColor="#ffffff"
                          size="md"
                          fontSize={14}
                          onChange={(e) => handleChangeRegion(e.target.value)}
                          value={values.macroRegionId}
                          _focus={{ borderColor: "none" }}
                          name={"macroRegionId"}
                          placeholder={
                            t("pleaseSelectRegionFromList") as string
                          }
                          disabled={authStore.readOnlyUser}
                        >
                          {regionStore.macroRegions?.map(
                            (value: IMacroRegion) => (
                              <option
                                style={{ color: "rgba(30, 82, 151, 0.8)" }}
                                value={value.id}
                                key={value.id}
                              >
                                {value.name}
                              </option>
                            )
                          )}
                        </Select>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel
                          color="newBlue.500"
                          fontSize={"md"}
                          fontWeight={500}
                        >
                          {t("station")}
                        </FormLabel>
                        <Select
                          id="selectStation"
                          borderRadius={8}
                          color={
                            values.stationRegion
                              ? "rgba(30, 82, 151, 0.8)"
                              : "gray.400"
                          }
                          fontSize={14}
                          backgroundColor="#ffffff"
                          size="md"
                          onChange={(e) => handleChangeStation(e.target.value)}
                          value={values.stationRegion}
                          _focus={{ borderColor: "none" }}
                          name={"stationRegion"}
                          placeholder={
                            t("pleaseSelectStationFromList") as string
                          }
                          disabled={authStore.readOnlyUser}
                        >
                          {oceanWeatherIntelligenceStore.stations?.map(
                            (value: IStation) => (
                              <option
                                style={{ color: "rgba(30, 82, 151, 0.8)" }}
                                value={`${value.station_id}-${value.region_id}`}
                                key={`${value.station_id}-${value.region_id}`}
                              >
                                {value.station_name}
                              </option>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                )}

                {locationType === "area" && (
                  <GeofenceMapContainer
                    values={values}
                    errors={{}}
                    touched={{}}
                    setFieldValue={formik.setFieldValue}
                    selectedGeofence={selectedGeofence}
                    handleDrawingMode={handleDrawingMode}
                    handleGeoJsonLoaded={handleGeoJsonLoaded}
                    t={t}
                    clearGeofence={clearGeofence}
                    setIsGeofenceLoadedByGeoJson={setIsGeofenceLoadedByGeoJson}
                    areas={areas}
                    handleAreaSelection={handleAreaSelection}
                    handleEnableAreaSelector={handleEnableAreaSelector}
                    areaSelectorVisible={areaSelectorVisible}
                    hideSaveAreaButtonOnMap={areaSelectorVisible}
                    showGeofenceError={
                      isGeofenceLoadedByGeoJson && isGeofenceInvalid
                    }
                    onBackToTypeSelection={handleBack}
                  />
                )}
              </Stack>
            </DrawerBody>

            {!authStore.readOnlyUser && (locationType || isEditing) && (
              <Box px={6} py={4} borderTopWidth="1px">
                <Flex gap={3} justifyContent="flex-end">
                  <Button
                    variant="ghost"
                    color="newBlue.500"
                    onClick={onClose}
                    size="md"
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    layerStyle="ocean"
                    _hover={{ layerStyle: "ocean" }}
                    onClick={handleSaveClick}
                    size="md"
                    isDisabled={isGeofenceInvalid}
                  >
                    {confirmButtonText}
                  </Button>
                </Flex>
              </Box>
            )}
          </DrawerContent>
        </Drawer>

        <LocationTypeChangeModal
          isOpen={isModalOpen}
          onClose={onModalClose}
          onConfirm={handleConfirmChange}
        />
      </>
    );
  };

export default observer(OperationRuleLocationDrawer);
